import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  getExpediteMaterials,
  addExpediteMaterial,
  resetExpediteMaterials,
  saveExpediteMaterials,
  updateExpediteMaterial,
} from '~/store/expedite/actions';
import { getMaterialsGroups, getMaterials } from '~/store/manager/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import styles from './styles';

const ExpediteMaterialsTab = ({ classes }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const expediteMaterials = useSelector(state => state.expedite.expediteMaterials);
  const materialsGroups = useSelector(state => state.manager.materialsGroups);
  const materials = useSelector(state => state.manager.materials);

  useEffect(() => {
    dispatch(getExpediteMaterials());
    dispatch(getMaterialsGroups({ all: true }));
    dispatch(getMaterials({ all: true }));
  }, [dispatch]);

  const validateEmptyFields = (rowData, key) => {
    const value = String(rowData[key])?.trim() || '';
    if (!value || value === '0' || value === '') {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }
    return { isValid: true };
  };

  const validateUniqueFields = (rowData, allRows, key) => {
    const hasDuplicateField = allRows.filter(
      row => String(row[key])?.trim() === String(rowData[key])?.trim()
          && row.id !== rowData.id,
    );
    if (hasDuplicateField.length > 0) {
      return { isValid: false, helperText: translate('validation:DuplicatedRegister') };
    }
    return { isValid: true };
  };

  const validateExternalProductId = (rowData) => {
    const emptyFieldsValidation = validateEmptyFields(rowData, 'external_product_id');
    if (!emptyFieldsValidation.isValid) {
      return emptyFieldsValidation;
    }
    const onlyNumbersRegex = /^[0-9]+$/;
    const isNotANumber = !onlyNumbersRegex.test(rowData.external_product_id);
    const maxLength = 20;
    const isTooLong = String(rowData.external_product_id).length > maxLength;

    if (isNotANumber) {
      return { isValid: false, helperText: translate('validation:JustNumbers') };
    }
    if (isTooLong) {
      return { isValid: false, helperText: translate('validation:maxLength20') };
    }

    const uniqueFieldsValidation = validateUniqueFields(rowData, expediteMaterials, 'external_product_id');
    if (!uniqueFieldsValidation.isValid) {
      return uniqueFieldsValidation;
    }

    return { isValid: true };
  };

  const validateMaterial = (rowData) => {
    const emptyFieldsValidation = validateEmptyFields(rowData, 'material_id');
    if (!emptyFieldsValidation.isValid) return emptyFieldsValidation;

    const uniqueFieldsValidation = validateUniqueFields(rowData, expediteMaterials, 'material_id');
    if (!uniqueFieldsValidation.isValid) return uniqueFieldsValidation;

    return { isValid: true };
  };

  const columns = [
    {
      title: translate('common:MaterialGroup'),
      field: 'material_group_id',
      lookup: materialsGroups,
      validate: rowData => validateEmptyFields(rowData, 'material_group_id'),
      customOnChange: () => ({ material_id: '0' }),
      required: true,
      customType: 'filterLookup',
    },
    {
      title: translate('common:Material'),
      field: 'material_id',
      lookup: materials,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => (
        String(lookup.id_group) === String(rowData.material_group_id)
      ),
      validate: rowData => validateMaterial(rowData),
      required: true,
    },
    {
      title: translate('common:ExternalMaterialId'),
      field: 'external_product_id',
      validate: rowData => validateExternalProductId(rowData),
      required: true,
    },
  ];

  const handleOnAdd = (newMaterial) => {
    dispatch(addExpediteMaterial(newMaterial));
  };

  const handleOnChange = (updatedMaterialList) => {
    updatedMaterialList.forEach((material) => {
      dispatch(updateExpediteMaterial(material));
    });
  };

  const handleDiscard = () => {
    dispatch(resetExpediteMaterials());
  };

  const handleOnApply = () => {
    dispatch(saveExpediteMaterials(expediteMaterials));
  };

  return (
    <div className={classes.container}>
      <PF2MDataTable
        title={translate('common:Materials')}
        columns={columns}
        data={expediteMaterials}
        options={{
          showTitle: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          filtering: true,
          search: true,
          selection: false,
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          hideDelete: true,
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 250px)',
        }}
        onAdd={handleOnAdd}
        onChange={handleOnChange}
        onDiscard={handleDiscard}
        onApply={handleOnApply}
      />
    </div>
  );
};

ExpediteMaterialsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

ExpediteMaterialsTab.defaultProps = {};

export default withStyles(styles)(ExpediteMaterialsTab);
